import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  AppSvgIconComponent,
  OverlayService,
  SignalButton,
  SignalRadio,
  SignalTextField,
  State,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import { DeleteDataLibraryVariableMode } from '@core/api/workspace/models/deleteDataLibraryVariableMode';
import type { DeleteDataLibraryVariableApiResponse } from '@core/api/workspace/models/deleteDataLibraryVariableApiResponse';
import type { ListDataLibraryVariableUsagesApiResponse } from '@core/api/workspace/models/listDataLibraryVariableUsagesApiResponse';
import { VariableUsageOverlayComponent } from '../variable-usage-overlay/variable-usage-overlay';

export interface DeleteVariableOverlayData {
  id: string;
  name: string;
}

export type DeleteVariableOverlayResult = { id: string };

const CONFIRM_TEXT = 'delete';

@Component({
  selector: 'app-delete-variable-overlay',
  imports: [
    AppSvgIconComponent,
    SignalButton,
    SignalRadio,
    SignalTextField,
  ],
  templateUrl: './delete-variable-overlay.html',
  styleUrl: './delete-variable-overlay.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeleteVariableOverlayComponent
  extends BaseComponent
  implements OnInit
{
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly overlayService = inject(OverlayService);
  private readonly dialogRef = inject(
    DialogRef<DeleteVariableOverlayResult | null>,
  );
  private readonly data = inject<DeleteVariableOverlayData>(DIALOG_DATA);

  readonly DeleteDataLibraryVariableMode = DeleteDataLibraryVariableMode;

  readonly usagesState =
    new State<ListDataLibraryVariableUsagesApiResponse>();
  readonly deleteState =
    new State<DeleteDataLibraryVariableApiResponse>();

  readonly variableName = this.data.name;
  readonly title = `Delete variable “${this.data.name}”`;

  readonly mode = signal<DeleteDataLibraryVariableMode>(
    DeleteDataLibraryVariableMode.retain,
  );
  readonly confirmInput = signal('');

  readonly usages = computed(
    () => this.usagesState.response()?.data?.items ?? [],
  );
  readonly totalCount = computed(
    () => this.usagesState.response()?.data?.totalCount ?? 0,
  );
  readonly hasUsages = computed(() => this.totalCount() > 0);
  readonly usagesReady = computed(() => this.usagesState.success());
  readonly usageSummary = computed(() => {
    const count = this.totalCount();
    if (count === 1) {
      return 'This variable is used in 1 place';
    }
    return `This variable is used in ${count} places`;
  });
  readonly canDelete = computed(
    () =>
      this.usagesReady() && this.confirmInput().trim() === CONFIRM_TEXT,
  );

  ngOnInit(): void {
    this.executeRequest({
      state: this.usagesState,
      request: this.userAPIService.listDataLibraryVariableUsages(this.data.id),
      handleError: true,
    });
  }

  openUsageOverlay(): void {
    this.overlayService.openModal(VariableUsageOverlayComponent, {
      data: {
        variableName: this.variableName,
        usages: this.usages(),
      },
      panelClass: ['variable-usage-overlay-panel'],
      fitContent: true,
    });
  }

  setMode(mode: string | null | undefined): void {
    if (
      mode === DeleteDataLibraryVariableMode.retain ||
      mode === DeleteDataLibraryVariableMode.remove
    ) {
      this.mode.set(mode);
    }
  }

  close(): void {
    this.dialogRef.close(null);
  }

  onDelete(): void {
    if (!this.canDelete() || this.deleteState.loading()) {
      return;
    }

    const params = this.hasUsages()
      ? { mode: this.mode() }
      : undefined;

    this.executeRequest({
      state: this.deleteState,
      request: this.userAPIService.deleteDataLibraryVariable(
        this.data.id,
        params,
      ),
      handleError: true,
      onSuccess: (response) => {
        this.dialogRef.close({ id: response.data.id });
      },
    });
  }
}
